import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getStrainWithBatches,
  updateStrain,
  addBatch,
  updateBatch,
  deleteBatch,
} from "@/lib/admin-content.functions";
import { strainCoreSchema, batchInputSchema } from "@/lib/admin-content.schemas";
import { StrainForm } from "@/components/admin/StrainForm";
import { BatchForm } from "@/components/admin/BatchForm";

export const Route = createFileRoute("/_authenticated/_admin/admin/medcan/$id/edit")({
  component: EditMedCanPage,
  errorComponent: ({ error }) => <div style={{ padding: "2rem", color: "#ef4444" }}>Fehler: {(error as Error).message}</div>,
  notFoundComponent: () => <div style={{ padding: "2rem", color: "var(--text-muted)" }}>Nicht gefunden.</div>,
});

const cardStyle: React.CSSProperties = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-purple)",
  borderRadius: "var(--radius-sm)",
  padding: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  marginBottom: "1.5rem",
};

function EditMedCanPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchStrain = useServerFn(getStrainWithBatches);
  const submitStrain = useServerFn(updateStrain);
  const submitAddBatch = useServerFn(addBatch);
  const submitUpdateBatch = useServerFn(updateBatch);
  const submitDeleteBatch = useServerFn(deleteBatch);

  const [strainSubmitting, setStrainSubmitting] = useState(false);
  const [batchBusyId, setBatchBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);

  const { data: strain } = useSuspenseQuery({
    queryKey: ["strain", id],
    queryFn: () => fetchStrain({ data: { id } }),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["strain", id] });
    qc.invalidateQueries({ queryKey: ["admin-content"] });
  }

  return (
    <div style={{ maxWidth: 900, margin: "2rem auto", padding: "0 1.5rem" }}>
      <h1 style={{ color: "var(--accent-cyan)", marginBottom: "1rem" }}>MedCan bearbeiten</h1>

      <div style={cardStyle}>
        <h2 style={{ margin: 0, fontSize: "1rem", color: "var(--text-primary)" }}>Strain</h2>
        <StrainForm
          submitting={strainSubmitting}
          defaultValues={{
            name: strain.name ?? "",
            dominance: (strain.dominance ?? "Hybrid") as any,
            reference_profile: strain.reference_profile ? JSON.stringify(strain.reference_profile, null, 2) : "",
            published: !!strain.published,
          }}
          onSubmit={async (v) => {
            const parsed = strainCoreSchema.safeParse(v);
            if (!parsed.success) {
              toast.error("Eingabe ungültig", { description: parsed.error.issues.map(i => i.message).join("; ") });
              return;
            }
            setStrainSubmitting(true);
            try {
              await submitStrain({ data: { id, data: parsed.data } });
              toast.success("Strain gespeichert");
              invalidate();
            } catch (e: any) {
              toast.error("Speichern fehlgeschlagen", { description: e?.message ?? String(e) });
            } finally {
              setStrainSubmitting(false);
            }
          }}
        />
      </div>

      <h2 style={{ fontSize: "1.05rem", color: "var(--text-primary)" }}>Batches</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
        {(strain.strain_batches ?? []).map((b: any) => (
          <BatchForm
            key={b.id}
            mode="edit"
            heading={`Batch · ${b.review_date ?? ""}`}
            submitting={batchBusyId === b.id}
            defaultValues={{
              thc_percent: b.thc_percent?.toString() ?? "",
              cbd_percent: b.cbd_percent?.toString() ?? "",
              terpenes: b.terpenes ?? "",
              aroma: b.aroma ?? "",
              crossing: b.crossing ?? "",
              irradiated: !!b.irradiated,
              origin: b.origin ?? "",
              importer: b.importer ?? "",
              producer: b.producer ?? "",
              medical_effects: b.medical_effects ?? "",
              review_text: b.review_text ?? "",
              rating: b.rating?.toString() ?? "",
              review_date: b.review_date ?? new Date().toISOString().slice(0, 10),
            }}
            onSubmit={async (v) => {
              const parsed = batchInputSchema.safeParse(v);
              if (!parsed.success) {
                toast.error("Eingabe ungültig", { description: parsed.error.issues.map(i => i.message).join("; ") });
                return;
              }
              setBatchBusyId(b.id);
              try {
                await submitUpdateBatch({ data: { id: b.id, data: parsed.data } });
                toast.success("Batch gespeichert");
                invalidate();
              } catch (e: any) {
                toast.error("Speichern fehlgeschlagen", { description: e?.message ?? String(e) });
              } finally {
                setBatchBusyId(null);
              }
            }}
            onDelete={async () => {
              if (!confirm("Diesen Batch wirklich löschen?")) return;
              setBatchBusyId(b.id);
              try {
                await submitDeleteBatch({ data: { id: b.id } });
                toast.success("Batch gelöscht");
                invalidate();
              } catch (e: any) {
                toast.error("Löschen fehlgeschlagen", { description: e?.message ?? String(e) });
              } finally {
                setBatchBusyId(null);
              }
            }}
          />
        ))}
      </div>

      {adding ? (
        <BatchForm
          heading="Neuer Batch"
          submitting={addBusy}
          onSubmit={async (v) => {
            const parsed = batchInputSchema.safeParse(v);
            if (!parsed.success) {
              toast.error("Eingabe ungültig", { description: parsed.error.issues.map(i => i.message).join("; ") });
              return;
            }
            setAddBusy(true);
            try {
              await submitAddBatch({ data: { strainId: id, data: parsed.data } });
              toast.success("Batch angelegt");
              setAdding(false);
              invalidate();
            } catch (e: any) {
              toast.error("Speichern fehlgeschlagen", { description: e?.message ?? String(e) });
            } finally {
              setAddBusy(false);
            }
          }}
        />
      ) : (
        <button onClick={() => setAdding(true)} style={{
          background: "transparent", color: "var(--accent-cyan)", border: "1px dashed var(--accent-cyan)",
          borderRadius: 6, padding: "0.6rem 1rem", cursor: "pointer", alignSelf: "flex-start",
        }}>+ Batch hinzufügen</button>
      )}

      <div style={{ marginTop: "1.5rem" }}>
        <button onClick={() => navigate({ to: "/admin" })} style={{
          background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border-purple)",
          borderRadius: 6, padding: "0.5rem 0.9rem", cursor: "pointer",
        }}>Zurück zur Übersicht</button>
      </div>
    </div>
  );
}
